const firstMessage = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item);
      if (message) return message;
    }
    return '';
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'detail',
      'non_field_errors',
      'assignment',
      'email',
      'department',
      'designation',
      'phone',
      'alternate_phone',
      'academic_year',
      'class_level',
      'section',
      'subject',
    ];

    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const message = firstMessage(value[key]);
        if (message) return message;
      }
    }

    for (const nestedValue of Object.values(value)) {
      const message = firstMessage(nestedValue);
      if (message) return message;
    }
  }

  return '';
};

const flattenErrors = (value, path = '', output = {}) => {
  if (value === undefined || value === null) {
    return output;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    output[path || 'root'] = String(value);
    return output;
  }

  if (Array.isArray(value)) {
    const hasObjects = value.some(
      (item) => item && typeof item === 'object',
    );

    if (!hasObjects) {
      const message = firstMessage(value);
      if (message) output[path || 'root'] = message;
      return output;
    }

    value.forEach((item, index) => {
      flattenErrors(
        item,
        path ? `${path}.${index}` : String(index),
        output,
      );
    });

    return output;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, nestedValue]) => {
      const nextPath =
        key === 'non_field_errors'
          ? path || 'root'
          : path
            ? `${path}.${key}`
            : key;

      flattenErrors(nestedValue, nextPath, output);
    });
  }

  return output;
};

export const getStaffApiFieldErrors = (error) => {
  const payload = error?.response?.data;
  const errors =
    payload?.errors ||
    payload?.data?.errors ||
    (payload && !payload.success ? payload : null);

  return errors ? flattenErrors(errors) : {};
};

export const getStaffApiErrorMessage = (
  error,
  fallback = 'Unable to complete the request.',
) => {
  const status = error?.response?.status;
  const payload = error?.response?.data;

  const fieldMessage = firstMessage(
    payload?.errors || payload?.data?.errors,
  );

  if (fieldMessage) {
    return fieldMessage;
  }

  const directMessage = firstMessage(
    payload?.detail || payload?.error || payload?.message,
  );

  if (directMessage && directMessage !== 'Request failed.') {
    return directMessage;
  }

  if (status === 400) {
    return 'Please correct the entered information and try again.';
  }

  if (status === 401) {
    return 'Your login session has expired. Please sign in again.';
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (status === 404) {
    return 'The requested staff or academic record was not found.';
  }

  if (status >= 500) {
    return 'The server could not complete the request. Please try again.';
  }

  return error?.message || fallback;
};

export default getStaffApiErrorMessage;
